import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type TreeNodeData = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  children?: TreeNodeData[];
  disabled?: boolean;
  data?: Record<string, unknown>;
};

export type TreeViewSize = 'sm' | 'md' | 'lg';
export type TreeViewVariant = 'default' | 'bordered' | 'filled';
export type SelectionMode = 'none' | 'single' | 'multiple';

export interface TreeViewContextValue {
  expanded: Set<string>;
  selected: Set<string>;
  toggleExpand: (id: string) => void;
  toggleSelect: (id: string) => void;
  size: TreeViewSize;
  variant: TreeViewVariant;
  selectionMode: SelectionMode;
  showLines: boolean;
  showIcons: boolean;
  disabled: boolean;
}

const TreeViewContext = React.createContext<TreeViewContextValue | null>(null);

function useTreeViewContext(): TreeViewContextValue {
  const context = React.useContext(TreeViewContext);
  if (!context) {
    throw new Error('TreeView components must be used within a TreeView');
  }
  return context;
}

// ============================================================================
// TreeView Component
// ============================================================================

export interface TreeViewProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  size?: TreeViewSize;
  variant?: TreeViewVariant;
  selectionMode?: SelectionMode;
  defaultExpanded?: string[];
  expanded?: string[];
  onExpandedChange?: (expanded: string[]) => void;
  defaultSelected?: string[];
  selected?: string[];
  onSelectedChange?: (selected: string[]) => void;
  showLines?: boolean;
  showIcons?: boolean;
  disabled?: boolean;
}

export function TreeView({
  children,
  size = 'md',
  variant = 'default',
  selectionMode = 'none',
  defaultExpanded = [],
  expanded: controlledExpanded,
  onExpandedChange,
  defaultSelected = [],
  selected: controlledSelected,
  onSelectedChange,
  showLines = false,
  showIcons = true,
  disabled = false,
  className,
  ...props
}: TreeViewProps) {
  const [internalExpanded, setInternalExpanded] = React.useState<Set<string>>(
    new Set(defaultExpanded)
  );
  const [internalSelected, setInternalSelected] = React.useState<Set<string>>(
    new Set(defaultSelected)
  );

  const expanded = controlledExpanded ? new Set(controlledExpanded) : internalExpanded;
  const selected = controlledSelected ? new Set(controlledSelected) : internalSelected;

  const toggleExpand = React.useCallback(
    (id: string) => {
      const newExpanded = new Set(expanded);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      if (!controlledExpanded) {
        setInternalExpanded(newExpanded);
      }
      onExpandedChange?.(Array.from(newExpanded));
    },
    [expanded, controlledExpanded, onExpandedChange]
  );

  const toggleSelect = React.useCallback(
    (id: string) => {
      if (selectionMode === 'none') return;

      let newSelected: Set<string>;
      if (selectionMode === 'single') {
        newSelected = selected.has(id) ? new Set() : new Set([id]);
      } else {
        newSelected = new Set(selected);
        if (newSelected.has(id)) {
          newSelected.delete(id);
        } else {
          newSelected.add(id);
        }
      }

      if (!controlledSelected) {
        setInternalSelected(newSelected);
      }
      onSelectedChange?.(Array.from(newSelected));
    },
    [selectionMode, selected, controlledSelected, onSelectedChange]
  );

  const contextValue: TreeViewContextValue = {
    expanded,
    selected,
    toggleExpand,
    toggleSelect,
    size,
    variant,
    selectionMode,
    showLines,
    showIcons,
    disabled,
  };

  return (
    <TreeViewContext.Provider value={contextValue}>
      <div
        role="tree"
        aria-multiselectable={selectionMode === 'multiple'}
        className={clsx('w-full', className)}
        {...props}
      >
        {children}
      </div>
    </TreeViewContext.Provider>
  );
}

// ============================================================================
// TreeNode Component
// ============================================================================

export interface TreeNodeProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  level?: number;
}

export function TreeNode({
  id,
  label,
  icon,
  children,
  disabled: nodeDisabled = false,
  level = 0,
  className,
  onClick,
  ...props
}: TreeNodeProps) {
  const {
    expanded,
    selected,
    toggleExpand,
    toggleSelect,
    size,
    variant,
    selectionMode,
    showLines,
    showIcons,
    disabled: treeDisabled,
  } = useTreeViewContext();

  const isExpanded = expanded.has(id);
  const isSelected = selected.has(id);
  const isDisabled = treeDisabled || nodeDisabled;
  const hasChildren = React.Children.count(children) > 0;

  const sizeClasses = {
    sm: 'py-0.5 text-sm',
    md: 'py-1 text-base',
    lg: 'py-1.5 text-lg',
  };

  const variantClasses = {
    default: isSelected
      ? 'bg-blue-100 dark:bg-blue-900/30'
      : 'hover:bg-gray-100 dark:hover:bg-gray-800',
    bordered: clsx(
      'border rounded',
      isSelected
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
        : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
    ),
    filled: isSelected
      ? 'bg-blue-500 text-white'
      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isDisabled) return;
    if (selectionMode !== 'none') {
      toggleSelect(id);
    }
    onClick?.(e);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDisabled && hasChildren) {
      toggleExpand(id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (selectionMode !== 'none') {
        toggleSelect(id);
      }
    } else if (e.key === 'ArrowRight' && hasChildren && !isExpanded) {
      e.preventDefault();
      toggleExpand(id);
    } else if (e.key === 'ArrowLeft' && hasChildren && isExpanded) {
      e.preventDefault();
      toggleExpand(id);
    }
  };

  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={selectionMode !== 'none' ? isSelected : undefined}
      aria-disabled={isDisabled}
      className={clsx('select-none', className)}
      {...props}
    >
      <div
        className={clsx(
          'flex items-center gap-1 px-2 rounded cursor-pointer transition-colors',
          sizeClasses[size],
          variantClasses[variant],
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
      >
        {showLines && level > 0 && (
          <span className="w-4 h-4 border-l border-b border-gray-300 dark:border-gray-600" />
        )}
        <button
          type="button"
          onClick={handleExpandClick}
          className={clsx(
            'w-4 h-4 flex items-center justify-center',
            hasChildren ? 'visible' : 'invisible'
          )}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          tabIndex={-1}
        >
          <svg
            className={clsx(
              'w-3 h-3 transition-transform',
              isExpanded && 'rotate-90'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        {showIcons && icon && <span className="w-5 h-5 flex-shrink-0">{icon}</span>}
        <span className="truncate">{label}</span>
      </div>
      {hasChildren && isExpanded && (
        <div role="group" className="ml-0">
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<TreeNodeProps>, {
                level: level + 1,
              });
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tree Component (Simplified API)
// ============================================================================

export interface TreeProps extends Omit<TreeViewProps, 'children'> {
  data: TreeNodeData[];
  renderIcon?: (node: TreeNodeData) => React.ReactNode;
  onNodeClick?: (node: TreeNodeData) => void;
}

export function Tree({
  data,
  renderIcon,
  onNodeClick,
  ...props
}: TreeProps) {
  const renderNode = (node: TreeNodeData): React.ReactNode => (
    <TreeNode
      key={node.id}
      id={node.id}
      label={node.label}
      icon={renderIcon?.(node) || node.icon}
      disabled={node.disabled}
      onClick={() => onNodeClick?.(node)}
    >
      {node.children?.map(renderNode)}
    </TreeNode>
  );

  return <TreeView {...props}>{data.map(renderNode)}</TreeView>;
}

// ============================================================================
// FileTree Component
// ============================================================================

const FolderIcon = ({ open }: { open?: boolean }) => (
  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
    {open ? (
      <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z" />
    ) : (
      <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
    )}
  </svg>
);

const FileIcon = ({ extension }: { extension?: string }) => {
  const colors: Record<string, string> = {
    js: 'text-yellow-500',
    ts: 'text-blue-500',
    tsx: 'text-blue-400',
    jsx: 'text-blue-300',
    css: 'text-pink-500',
    json: 'text-green-500',
    md: 'text-gray-500 dark:text-gray-400',
  };

  return (
    <svg
      className={clsx('w-4 h-4', colors[extension || ''] || 'text-gray-400 dark:text-gray-500')}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
    </svg>
  );
};

export interface FileTreeNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  children?: FileTreeNode[];
}

export interface FileTreeProps extends Omit<TreeViewProps, 'children'> {
  data: FileTreeNode[];
  onFileSelect?: (file: FileTreeNode) => void;
  onFolderToggle?: (folder: FileTreeNode, expanded: boolean) => void;
}

export function FileTree({
  data,
  onFileSelect,
  onFolderToggle,
  ...props
}: FileTreeProps) {
  const renderFileNode = (node: FileTreeNode): React.ReactNode => {
    const extension = node.name.split('.').pop();
    const isFolder = node.type === 'folder';

    return (
      <TreeNode
        key={node.id}
        id={node.id}
        label={node.name}
        icon={
          isFolder ? (
            <FolderIcon />
          ) : (
            <FileIcon extension={extension} />
          )
        }
        onClick={() => {
          if (node.type === 'file') {
            onFileSelect?.(node);
          }
        }}
      >
        {node.children?.map(renderFileNode)}
      </TreeNode>
    );
  };

  return (
    <TreeView selectionMode="single" {...props}>
      {data.map(renderFileNode)}
    </TreeView>
  );
}

// ============================================================================
// DirectoryTree Component
// ============================================================================

export interface DirectoryTreeProps extends FileTreeProps {
  showCheckboxes?: boolean;
  checkable?: boolean;
  checkedKeys?: string[];
  onCheck?: (checkedKeys: string[]) => void;
}

export function DirectoryTree({
  data,
  showCheckboxes = false,
  checkable = false,
  checkedKeys = [],
  onCheck,
  onFileSelect,
  ...props
}: DirectoryTreeProps) {
  const [checked, setChecked] = React.useState<Set<string>>(new Set(checkedKeys));

  React.useEffect(() => {
    setChecked(new Set(checkedKeys));
  }, [checkedKeys]);

  const handleCheck = (id: string) => {
    const newChecked = new Set(checked);
    if (newChecked.has(id)) {
      newChecked.delete(id);
    } else {
      newChecked.add(id);
    }
    setChecked(newChecked);
    onCheck?.(Array.from(newChecked));
  };

  const renderDirectoryNode = (node: FileTreeNode): React.ReactNode => {
    const extension = node.name.split('.').pop();
    const isFolder = node.type === 'folder';
    const isChecked = checked.has(node.id);

    return (
      <TreeNode
        key={node.id}
        id={node.id}
        label={
          <span className="flex items-center gap-2">
            {(showCheckboxes || checkable) && (
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => handleCheck(node.id)}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-700"
              />
            )}
            <span>{node.name}</span>
          </span>
        }
        icon={
          isFolder ? (
            <FolderIcon />
          ) : (
            <FileIcon extension={extension} />
          )
        }
        onClick={() => {
          if (node.type === 'file') {
            onFileSelect?.(node);
          }
        }}
      >
        {node.children?.map(renderDirectoryNode)}
      </TreeNode>
    );
  };

  return (
    <TreeView {...props}>
      {data.map(renderDirectoryNode)}
    </TreeView>
  );
}

// ============================================================================
// Hierarchy Component (Org Chart Style)
// ============================================================================

export interface HierarchyNode {
  id: string;
  label: string;
  description?: string;
  avatar?: string;
  children?: HierarchyNode[];
}

export interface HierarchyProps extends React.HTMLAttributes<HTMLDivElement> {
  data: HierarchyNode;
  orientation?: 'vertical' | 'horizontal';
  nodeClassName?: string;
  lineClassName?: string;
}

export function Hierarchy({
  data,
  orientation = 'vertical',
  nodeClassName,
  lineClassName,
  className,
  ...props
}: HierarchyProps) {
  const renderNode = (node: HierarchyNode, isRoot: boolean = false): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div
        key={node.id}
        className={clsx(
          'flex',
          orientation === 'vertical' ? 'flex-col items-center' : 'flex-row items-start'
        )}
      >
        {!isRoot && (
          <div
            className={clsx(
              'bg-gray-300 dark:bg-gray-600',
              orientation === 'vertical' ? 'w-px h-6' : 'w-6 h-px',
              lineClassName
            )}
          />
        )}
        <div
          className={clsx(
            'flex flex-col items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm',
            nodeClassName
          )}
        >
          {node.avatar && (
            <img
              src={node.avatar}
              alt={node.label}
              className="w-10 h-10 rounded-full mb-2"
            />
          )}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {node.label}
          </span>
          {node.description && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {node.description}
            </span>
          )}
        </div>
        {hasChildren && (
          <>
            <div
              className={clsx(
                'bg-gray-300 dark:bg-gray-600',
                orientation === 'vertical' ? 'w-px h-6' : 'w-6 h-px',
                lineClassName
              )}
            />
            <div
              className={clsx(
                'flex gap-4',
                orientation === 'vertical' ? 'flex-row' : 'flex-col'
              )}
            >
              {node.children?.map((child) => renderNode(child))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={clsx('inline-flex', className)}
      role="tree"
      aria-label="Hierarchy"
      {...props}
    >
      {renderNode(data, true)}
    </div>
  );
}

// ============================================================================
// TreeSelect Component
// ============================================================================

export interface TreeSelectProps {
  data: TreeNodeData[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
}

export function TreeSelect({
  data,
  value,
  onChange,
  multiple = false,
  placeholder = 'Select...',
  disabled = false,
  className,
  searchable = false,
}: TreeSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  const findNodeById = (nodes: TreeNodeData[], id: string): TreeNodeData | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedLabels = selectedValues
    .map((v) => findNodeById(data, v)?.label || v)
    .join(', ');

  const handleSelect = (id: string) => {
    if (multiple) {
      const newValue = selectedValues.includes(id)
        ? selectedValues.filter((v) => v !== id)
        : [...selectedValues, id];
      onChange?.(newValue);
    } else {
      onChange?.(id);
      setIsOpen(false);
    }
  };

  const filterNodes = (nodes: TreeNodeData[], searchTerm: string): TreeNodeData[] => {
    return nodes.reduce<TreeNodeData[]>((acc, node) => {
      const matchesSearch = node.label.toLowerCase().includes(searchTerm.toLowerCase());
      const filteredChildren = node.children
        ? filterNodes(node.children, searchTerm)
        : [];

      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children,
        });
      }
      return acc;
    }, []);
  };

  const displayData = search ? filterNodes(data, search) : data;

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderSelectNode = (node: TreeNodeData, level: number = 0): React.ReactNode => (
    <div key={node.id}>
      <button
        type="button"
        className={clsx(
          'w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2',
          selectedValues.includes(node.id) && 'bg-blue-50 dark:bg-blue-900/30',
          node.disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{ paddingLeft: `${level * 16 + 12}px` }}
        onClick={() => !node.disabled && handleSelect(node.id)}
        disabled={node.disabled}
      >
        {multiple && (
          <input
            type="checkbox"
            checked={selectedValues.includes(node.id)}
            onChange={() => {}}
            className="w-4 h-4"
          />
        )}
        {node.icon && <span className="w-4 h-4">{node.icon}</span>}
        <span>{node.label}</span>
      </button>
      {node.children?.map((child) => renderSelectNode(child, level + 1))}
    </div>
  );

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={clsx(
          'w-full px-3 py-2 text-left border rounded-lg flex items-center justify-between',
          'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600',
          'focus:outline-none focus:ring-2 focus:ring-blue-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={clsx(!selectedLabels && 'text-gray-400 dark:text-gray-500')}>
          {selectedLabels || placeholder}
        </span>
        <svg
          className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {searchable && (
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 text-sm border rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}
          {displayData.map((node) => renderSelectNode(node))}
          {displayData.length === 0 && (
            <div className="px-3 py-2 text-gray-500 text-sm dark:text-gray-400">No results found</div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// NestedList Component
// ============================================================================

export interface NestedListItem {
  id: string;
  content: React.ReactNode;
  children?: NestedListItem[];
}

export interface NestedListProps extends React.HTMLAttributes<HTMLUListElement> {
  items: NestedListItem[];
  ordered?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export function NestedList({
  items,
  ordered = false,
  collapsible = false,
  defaultCollapsed = false,
  className,
  ...props
}: NestedListProps) {
  const [collapsed, setCollapsed] = React.useState<Set<string>>(
    defaultCollapsed ? new Set(items.map((i) => i.id)) : new Set()
  );

  const toggleCollapse = (id: string) => {
    const newCollapsed = new Set(collapsed);
    if (newCollapsed.has(id)) {
      newCollapsed.delete(id);
    } else {
      newCollapsed.add(id);
    }
    setCollapsed(newCollapsed);
  };

  const renderItem = (item: NestedListItem): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isCollapsed = collapsed.has(item.id);
    const ListTag = ordered ? 'ol' : 'ul';

    return (
      <li key={item.id} className="py-1">
        <div className="flex items-center gap-2">
          {collapsible && hasChildren && (
            <button
              type="button"
              onClick={() => toggleCollapse(item.id)}
              className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              <svg
                className={clsx('w-3 h-3 transition-transform', !isCollapsed && 'rotate-90')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
          <span>{item.content}</span>
        </div>
        {hasChildren && !isCollapsed && (
          <ListTag className="ml-6 mt-1">{item.children?.map(renderItem)}</ListTag>
        )}
      </li>
    );
  };

  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <ListTag
      className={clsx(
        ordered ? 'list-decimal' : 'list-disc',
        'list-inside',
        className
      )}
      {...props}
    >
      {items.map(renderItem)}
    </ListTag>
  );
}

// ============================================================================
// Breadcrumb Tree Component
// ============================================================================

export interface BreadcrumbTreeProps {
  path: TreeNodeData[];
  onNavigate?: (node: TreeNodeData, index: number) => void;
  separator?: React.ReactNode;
  className?: string;
}

export function BreadcrumbTree({
  path,
  onNavigate,
  separator = '/',
  className,
}: BreadcrumbTreeProps) {
  return (
    <nav className={clsx('flex items-center gap-2 text-sm', className)} aria-label="Breadcrumb">
      {path.map((node, index) => (
        <React.Fragment key={node.id}>
          {index > 0 && (
            <span className="text-gray-400 dark:text-gray-500">{separator}</span>
          )}
          <button
            type="button"
            onClick={() => onNavigate?.(node, index)}
            className={clsx(
              'hover:text-blue-500 transition-colors',
              index === path.length - 1
                ? 'text-gray-900 dark:text-gray-100 font-medium'
                : 'text-gray-500 dark:text-gray-400'
            )}
          >
            {node.icon && <span className="mr-1">{node.icon}</span>}
            {node.label}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
}

// ============================================================================
// useTree Hook
// ============================================================================

export interface UseTreeOptions {
  defaultExpanded?: string[];
  defaultSelected?: string[];
  onExpandedChange?: (expanded: string[]) => void;
  onSelectedChange?: (selected: string[]) => void;
  selectionMode?: SelectionMode;
}

export interface UseTreeReturn {
  expanded: string[];
  selected: string[];
  isExpanded: (id: string) => boolean;
  isSelected: (id: string) => boolean;
  expand: (id: string) => void;
  collapse: (id: string) => void;
  toggle: (id: string) => void;
  expandAll: (ids: string[]) => void;
  collapseAll: () => void;
  select: (id: string) => void;
  deselect: (id: string) => void;
  toggleSelect: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
}

export function useTree({
  defaultExpanded = [],
  defaultSelected = [],
  onExpandedChange,
  onSelectedChange,
  selectionMode = 'none',
}: UseTreeOptions = {}): UseTreeReturn {
  const [expanded, setExpanded] = React.useState<string[]>(defaultExpanded);
  const [selected, setSelected] = React.useState<string[]>(defaultSelected);

  const isExpanded = React.useCallback((id: string) => expanded.includes(id), [expanded]);
  const isSelected = React.useCallback((id: string) => selected.includes(id), [selected]);

  const expand = React.useCallback(
    (id: string) => {
      const newExpanded = [...new Set([...expanded, id])];
      setExpanded(newExpanded);
      onExpandedChange?.(newExpanded);
    },
    [expanded, onExpandedChange]
  );

  const collapse = React.useCallback(
    (id: string) => {
      const newExpanded = expanded.filter((e) => e !== id);
      setExpanded(newExpanded);
      onExpandedChange?.(newExpanded);
    },
    [expanded, onExpandedChange]
  );

  const toggle = React.useCallback(
    (id: string) => {
      if (isExpanded(id)) {
        collapse(id);
      } else {
        expand(id);
      }
    },
    [isExpanded, expand, collapse]
  );

  const expandAll = React.useCallback(
    (ids: string[]) => {
      const newExpanded = [...new Set([...expanded, ...ids])];
      setExpanded(newExpanded);
      onExpandedChange?.(newExpanded);
    },
    [expanded, onExpandedChange]
  );

  const collapseAll = React.useCallback(() => {
    setExpanded([]);
    onExpandedChange?.([]);
  }, [onExpandedChange]);

  const select = React.useCallback(
    (id: string) => {
      if (selectionMode === 'none') return;
      const newSelected = selectionMode === 'single' ? [id] : [...new Set([...selected, id])];
      setSelected(newSelected);
      onSelectedChange?.(newSelected);
    },
    [selectionMode, selected, onSelectedChange]
  );

  const deselect = React.useCallback(
    (id: string) => {
      const newSelected = selected.filter((s) => s !== id);
      setSelected(newSelected);
      onSelectedChange?.(newSelected);
    },
    [selected, onSelectedChange]
  );

  const toggleSelect = React.useCallback(
    (id: string) => {
      if (isSelected(id)) {
        deselect(id);
      } else {
        select(id);
      }
    },
    [isSelected, select, deselect]
  );

  const selectAll = React.useCallback(
    (ids: string[]) => {
      if (selectionMode !== 'multiple') return;
      const newSelected = [...new Set([...selected, ...ids])];
      setSelected(newSelected);
      onSelectedChange?.(newSelected);
    },
    [selectionMode, selected, onSelectedChange]
  );

  const deselectAll = React.useCallback(() => {
    setSelected([]);
    onSelectedChange?.([]);
  }, [onSelectedChange]);

  return {
    expanded,
    selected,
    isExpanded,
    isSelected,
    expand,
    collapse,
    toggle,
    expandAll,
    collapseAll,
    select,
    deselect,
    toggleSelect,
    selectAll,
    deselectAll,
  };
}
